// script.js
import * as THREE from './three.module.js';
import { OrbitControls } from './OrbitControls.js';
import jsonData from './locationsfinal.json';

// Scene setup
const scene = new THREE.Scene();
scene.background = new THREE.Color(0x1a1a2e);
scene.fog = new THREE.Fog(0x1a1a2e, 1000, 5000);

// Camera setup
const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 5000);
camera.position.set(-350, 1650, 1850);

// Renderer setup with antialiasing
const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.setPixelRatio(window.devicePixelRatio);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;

const container = document.getElementById('canvas-container');
container.appendChild(renderer.domElement);

// Enhanced lighting
const ambientLight = new THREE.AmbientLight(0xffffff, 0.6);
scene.add(ambientLight);

const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
directionalLight.position.set(1000, 2000, 1000);
directionalLight.castShadow = true;
directionalLight.shadow.camera.left = -2000;
directionalLight.shadow.camera.right = 2000;
directionalLight.shadow.camera.top = 2000;
directionalLight.shadow.camera.bottom = -2000;
scene.add(directionalLight);

const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0x444444, 0.4);
scene.add(hemisphereLight);

// Controls
const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.05;
controls.screenSpacePanning = false;
controls.minDistance = 100;
controls.maxDistance = 3000;
controls.maxPolarAngle = Math.PI / 2;

// Grid helper
const gridHelper = new THREE.GridHelper(8000, 80, 0x667eea, 0x444444);
gridHelper.position.y = 0;
gridHelper.position.x = 2500;
scene.add(gridHelper);

// Axes helper
const axesHelper = new THREE.AxesHelper(200);
axesHelper.position.y = 5;
scene.add(axesHelper);

// Data structures
const cubes = [];
let selectedObject = null;
let enableColorMode = false;
let enableEmptyMode = false;

// Statistics
let totalLocations = 0;
let emptyLocations = 0;
let totalVolume = 0;

// Create warehouse locations
jsonData.forEach(item => {
    totalLocations++;
    totalVolume += parseFloat(item.volume) || 0;
    if (item.volume === 0 || item.volume === '0') {
        emptyLocations++;
    }

    const geometry = new THREE.BoxGeometry(item.loclen, item.lochgt, item.locwid);
    
    // Enhanced material with better shading
    const material = new THREE.MeshStandardMaterial({
        color: 0x6b7280,
        transparent: true,
        opacity: 0.85,
        metalness: 0.3,
        roughness: 0.7,
        emissive: 0x000000,
        emissiveIntensity: 0
    });

    const cube = new THREE.Mesh(geometry, material);
    cube.position.set(item.x, item.y, item.z);
    cube.castShadow = true;
    cube.receiveShadow = true;
    
    scene.add(cube);
    cubes.push(cube);

    // Store metadata
    cube.userData.arecod = item.arecod;
    cube.userData.stoloc = item.stoloc;
    cube.userData.loclen = item.loclen;
    cube.userData.locwid = item.locwid;
    cube.userData.lochgt = item.lochgt;
    cube.userData.volume = item.volume;
    cube.userData.item = item.Item != null ? item.Item : (item.item != null ? item.item : null);
});

// Update statistics display
document.getElementById('totalLocs').textContent = totalLocations;
document.getElementById('emptyLocs').textContent = emptyLocations;
document.getElementById('avgVolume').textContent = (totalVolume / totalLocations).toFixed(1) + '%';

// Hide loading indicator
document.getElementById('loading').style.display = 'none';

// Enhanced floor
const floorGeometry = new THREE.PlaneGeometry(8000, 6000);
const floorMaterial = new THREE.MeshStandardMaterial({ 
    color: 0x2c2c3e,
    metalness: 0.1,
    roughness: 0.8
});
const floor = new THREE.Mesh(floorGeometry, floorMaterial);
floor.rotation.x = Math.PI / 2;
floor.position.x = 2500;
floor.receiveShadow = true;
scene.add(floor);

// Raycasting for interaction
const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();

// Info panel element
const infoElement = document.getElementById('info');
const infoContent = document.getElementById('infoContent');

// Convert client coords to NDC using canvas rect (handles canvas position/size correctly)
function setMouseFromEvent(event) {
    const canvas = renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
}

// Mouse move handler for hover effects
function onMouseMove(event) {
    setMouseFromEvent(event);
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(cubes);

    // Reset all cubes to default state (only if not in special mode)
    cubes.forEach(cube => {
        if (cube !== selectedObject) {
            cube.material.emissiveIntensity = 0;
        }
    });

    if (intersects.length > 0) {
        const hoveredObject = intersects[0].object;
        if (hoveredObject !== selectedObject) {
            hoveredObject.material.emissive.setHex(0x667eea);
            hoveredObject.material.emissiveIntensity = 0.3;
        }
        document.body.style.cursor = 'pointer';
    } else {
        document.body.style.cursor = 'default';
    }
}

// Click handler for selection (runs on canvas only)
function onPointerDown(event) {
    if (event.button !== 0) return; // left button only
    setMouseFromEvent(event);
    raycaster.setFromCamera(mouse, camera);
    const intersects = raycaster.intersectObjects(cubes);

    if (intersects.length > 0) {
        // Deselect previous
        if (selectedObject) {
            selectedObject.material.emissive.setHex(0x000000);
            selectedObject.material.emissiveIntensity = 0;
        }

        // Select new
        selectedObject = intersects[0].object;
        selectedObject.material.emissive.setHex(0x00ffff);
        selectedObject.material.emissiveIntensity = 0.5;

        // Show info panel
        infoElement.classList.add('visible');
        
        const volume = parseFloat(selectedObject.userData.volume) || 0;
        const volumeColor = volume === 0 ? '#ef4444' : volume < 50 ? '#10b981' : '#f59e0b';
        const itemDisplay = selectedObject.userData.item != null
            ? String(selectedObject.userData.item)
            : '—';
        
        infoContent.innerHTML = `
            <div class="info-row">
                <span class="info-label">Name:</span>
                <span class="info-value">${selectedObject.userData.stoloc || '—'}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Area Code:</span>
                <span class="info-value">${selectedObject.userData.arecod || '—'}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Dimensions:</span>
                <span class="info-value">${selectedObject.userData.loclen} × ${selectedObject.userData.locwid} × ${selectedObject.userData.lochgt}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Item:</span>
                <span class="info-value">${itemDisplay}</span>
            </div>
            <div class="info-row">
                <span class="info-label">Qty:</span>
                <span class="info-value" style="color: ${volumeColor}; font-weight: 700;">${volume}</span>
            </div>
        `;
    } else {
        // Deselect if clicking empty space
        if (selectedObject) {
            selectedObject.material.emissive.setHex(0x000000);
            selectedObject.material.emissiveIntensity = 0;
            selectedObject = null;
        }
        infoElement.classList.remove('visible');
    }
}


// Event listeners: bind to canvas so we always get correct target and use canvas rect for NDC
const canvas = renderer.domElement;
canvas.addEventListener('mousemove', onMouseMove, false);
canvas.addEventListener('pointerdown', onPointerDown, false);

// View mode functions
function toggleColorMode() {
    enableColorMode = !enableColorMode;
    if (enableEmptyMode) {
        enableEmptyMode = false;
        document.getElementById('emptyMode').classList.remove('active');
    }
    
    const btn = document.getElementById('volumeMode');
    btn.classList.toggle('active');
    
    updateCubeColors();
}

function toggleEmptyMode() {
    enableEmptyMode = !enableEmptyMode;
    if (enableColorMode) {
        enableColorMode = false;
        document.getElementById('volumeMode').classList.remove('active');
    }
    
    const btn = document.getElementById('emptyMode');
    btn.classList.toggle('active');
    
    updateEmptyColors();
}

function updateCubeColors() {
    cubes.forEach(cube => {
        if (enableColorMode) {
            const volume = parseFloat(cube.userData.volume) || 0;
            const color = new THREE.Color();
            
            // Smooth color gradient from green (0%) to yellow (50%) to red (100%)
            if (volume <= 50) {
                // Green to Yellow
                color.setRGB(volume / 50, 1, 0);
            } else {
                // Yellow to Red
                color.setRGB(1, 1 - (volume - 50) / 50, 0);
            }
            
            cube.material.color.copy(color);
            cube.material.opacity = 0.85;
        } else {
            cube.material.color.setHex(0x6b7280);
            cube.material.opacity = 0.85;
        }
        
        // Preserve selection highlight
        if (cube === selectedObject) {
            cube.material.emissive.setHex(0x00ffff);
            cube.material.emissiveIntensity = 0.5;
        }
    });
}

function updateEmptyColors() {
    cubes.forEach(cube => {
        if (enableEmptyMode) {
            const volume = parseFloat(cube.userData.volume) || 0;
            if (volume === 0) {
                cube.material.color.setHex(0x3b82f6); // Blue for empty
                cube.material.opacity = 0.9;
            } else {
                cube.material.color.setHex(0x4b5563); // Dark gray for occupied
                cube.material.opacity = 0.3;
            }
        } else {
            cube.material.color.setHex(0x6b7280);
            cube.material.opacity = 0.85;
        }
        
        // Preserve selection highlight
        if (cube === selectedObject) {
            cube.material.emissive.setHex(0x00ffff);
            cube.material.emissiveIntensity = 0.5;
        }
    });
}

function resetView() {
    camera.position.set(-350, 1650, 1850);
    controls.target.set(0, 0, 0);
    controls.update();
}

// Close info panel when clicking the X button
document.getElementById('infoClose').addEventListener('click', () => {
    if (selectedObject) {
        selectedObject.material.emissive.setHex(0x000000);
        selectedObject.material.emissiveIntensity = 0;
        selectedObject = null;
    }
    infoElement.classList.remove('visible');
});

// Button event listeners
document.getElementById('volumeMode').addEventListener('click', toggleColorMode);
document.getElementById('emptyMode').addEventListener('click', toggleEmptyMode);
document.getElementById('resetView').addEventListener('click', resetView);

// Window resize handler
window.addEventListener('resize', () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
});

// Animation loop
function animate() {
    requestAnimationFrame(animate);
    controls.update();
    renderer.render(scene, camera);
}

animate();
